import { db } from "@db";
import type {
  GenbankReference,
  GnomuxFeature,
  GnomuxSequence,
  Topology,
} from "@db/content";
import {
  DEFAULT_CHUNK_SIZE,
  encodeNucleotides,
  getEncoder,
} from "@db/encoding";
import type { GnomuxFile } from "@db/objects";
import { computeContentHash } from "@db/operationLog";
import type { SequenceCheckpoint } from "@history/operations";

export type FeatureInput = Omit<GnomuxFeature, "sequenceId" | "createdAt">;

export type WriteNucleotideSequenceInput = {
  id?: string;
  name: string;
  sequence: string;
  ownerId: string;
  createdAt?: Date;
  topology?: Topology;
  description?: string;
  organism?: string;
  taxonomy?: string;
  accession?: string;
  version?: string;
  comment?: string;
  references?: GenbankReference[];
  customMetadata?: { key: string; value: string }[];
  features?: GnomuxFeature[];
};

type PreparedNucleotideSequence = {
  sequenceRecord: GnomuxSequence;
  encoded: Uint8Array;
  checkpoint: SequenceCheckpoint;
  features?: GnomuxFeature[];
};

async function prepareNucleotideSequence(
  input: WriteNucleotideSequenceInput,
): Promise<PreparedNucleotideSequence> {
  const { name, sequence: nucleotideSequence, ownerId } = input;
  const topology = input.topology ?? "linear";
  const sequenceId = input.id ?? crypto.randomUUID();
  const createdAt = input.createdAt ?? new Date();

  const encoded = encodeNucleotides(nucleotideSequence);
  const encode = getEncoder("nt");
  const contentHash = await computeContentHash(nucleotideSequence);

  const sequenceRecord: GnomuxSequence = {
    id: sequenceId,
    name,
    kind: "nt",
    length: nucleotideSequence.length,
    chunkSize: DEFAULT_CHUNK_SIZE,
    isLocked: false,
    createdAt,
    ownerId,
    topology,
    contentHash,
    description: input.description ?? "",
    organism: input.organism ?? "",
    taxonomy: input.taxonomy ?? "",
    accession: input.accession ?? "",
    version: input.version ?? "",
    comment: input.comment ?? "",
    references: input.references ?? [],
    customMetadata: input.customMetadata ?? [],
  };

  const checkpoint: SequenceCheckpoint = {
    id: crypto.randomUUID(),
    sequenceId,
    operationIndex: -1,
    contentHash,
    content: encode(nucleotideSequence),
    length: nucleotideSequence.length,
  };

  return { sequenceRecord, encoded, checkpoint, features: input.features };
}

/**
 * Prepare and write a nucleotide sequence record, encoded chunk, and initial
 * checkpoint.
 *
 * Async work (content hash) runs before the Dexie transaction. The returned
 * write function contains only synchronous Dexie puts and must be called inside
 * a transaction that includes db.sequences, db.sequenceChunks,
 * db.sequenceCheckpoints, and db.features.
 */
export async function prepareNucleotideSequenceWrite(
  input: WriteNucleotideSequenceInput,
): Promise<{ sequenceRecord: GnomuxSequence; write: () => Promise<void> }> {
  const { sequenceRecord, encoded, checkpoint, features } =
    await prepareNucleotideSequence(input);

  async function write(): Promise<void> {
    await db.sequences.put(sequenceRecord);
    await db.sequenceChunks.put({
      sequenceId: sequenceRecord.id,
      index: 0,
      data: encoded,
    });
    await db.sequenceCheckpoints.put(checkpoint);

    if (features && features.length > 0) {
      await db.features.bulkPut(features);
    }
  }

  return { sequenceRecord, write };
}

export type CreateNucleotideSequenceInput = {
  name: string;
  sequence: string;
  ownerId: string;
  topology?: Topology;
  description?: string;
  organism?: string;
  taxonomy?: string;
  accession?: string;
  version?: string;
  comment?: string;
  references?: GenbankReference[];
  customMetadata?: { key: string; value: string }[];
  features?: FeatureInput[];
};

export type CreateNucleotideSequenceResult = {
  sequence: GnomuxSequence;
  file: GnomuxFile;
};

export async function createNucleotideSequence(
  input: CreateNucleotideSequenceInput,
): Promise<CreateNucleotideSequenceResult> {
  const sequenceId = crypto.randomUUID();
  const fileId = crypto.randomUUID();
  const createdAt = new Date();

  const features: GnomuxFeature[] | undefined = input.features?.map((f) => ({
    ...f,
    sequenceId,
    createdAt,
  }));

  const { sequenceRecord, write } = await prepareNucleotideSequenceWrite({
    ...input,
    id: sequenceId,
    createdAt,
    features,
  });

  const file: GnomuxFile = {
    id: fileId,
    name: input.name,
    content: sequenceId,
    isLocked: false,
    parent: "root",
    type: "GnomuxSequence",
    createdAt,
    ownerId: input.ownerId,
  };

  await db.transaction(
    "rw",
    [
      db.sequences,
      db.sequenceChunks,
      db.sequenceCheckpoints,
      db.objects,
      db.features,
    ],
    async () => {
      await write();
      await db.objects.put(file);
    },
  );

  return { sequence: sequenceRecord, file };
}
